import { useState, useEffect, FC, useRef } from 'react';
import { Grid, TextField, Button, SvgIcon, FormControl, InputLabel, Select, MenuItem, Alert, Snackbar, SelectChangeEvent } from '@mui/material';
import { Search } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import StoresList from './StoresList';
import { Section } from '../../../components/ui/Section';
import { MarketStore, MarketStoreFilterOptions, Instance, Market } from '../../../app/services/api/orderManagementClient';
import { client } from '../../../app/services/api/orderManagementClient';
import Pagination from '../../../components/forms/Pagination';
import useLogError from '../../../hooks/useLogError';

type Props = {
  onCreateNew: () => void;
  onFilterChange?: (_instance: string, _market: string, searchTerm?: string) => void;
  initialInstance?: string;
  initialMarket?: string;
  initialSearchTerm?: string;
}

const StoresTab: FC<Props> = ({ onCreateNew, onFilterChange, initialInstance, initialMarket, initialSearchTerm }) => {
  const { t } = useTranslation('stores');
  const { logError } = useLogError();

  const [stores, setStores] = useState<MarketStore[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchInput, setSearchInput] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedInstance, setSelectedInstance] = useState<string>('');
  const [selectedMarket, setSelectedMarket] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [instances, setInstances] = useState<Instance[]>([]);
  const [markets, setMarkets] = useState<Market[]>([]);
  const isLoadingMarketsRef = useRef(false);

  // Snackbar states
  const [showSubmitStatusMessage, setShowSubmitStatusMessage] = useState<boolean>(false);
  const [isSubmittedSuccessfully, setIsSubmittedSuccessfully] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const pageSize = 20;

  useEffect(() => {
    loadInstances();
  }, []);

  useEffect(() => {
    if (initialInstance) setSelectedInstance(initialInstance);
    if (initialMarket) setSelectedMarket(initialMarket);
    if (initialSearchTerm !== undefined) {
      setSearchInput(initialSearchTerm);
      setSearchTerm(initialSearchTerm);
    }
  }, [initialInstance, initialMarket, initialSearchTerm]);
  
  useEffect(() => {
    if (selectedInstance || selectedInstance === 'all') {
      loadMarkets(selectedInstance);
    }
  }, [selectedInstance]);

  useEffect(() => {
    if (isLoadingMarketsRef.current) return;

    if (selectedInstance || selectedInstance === 'all')
    {
      loadStores();
    }
    else{
      setStores([]);
      setTotalPages(0);
      setTotalItems(0);
    }
  }, [currentPage, searchTerm, selectedInstance, selectedMarket]);

  const loadInstances = async () => {
    try {
      const response = await client.getInstances({
        pageNumber: 0,
        pageSize: 2147483647,
      });

      if (response.success && response.data) {
        setInstances(response.data.items || []);
      }
    } catch (error) {
      logError('Failed to load instances');
    }
  };

  const loadMarkets = async (instanceId: string) => {
    isLoadingMarketsRef.current = true;

    try {
      const response = await client.getMarkets({
        companyId: instanceId,
        pageNumber: 0,
        pageSize: 2147483647,
      });

      if (response.success && response.data) {
        setMarkets(response.data.items || []);
      }
      else {
        isLoadingMarketsRef.current = false;
      }
    } catch (error) {
      isLoadingMarketsRef.current = false; 
      logError('Failed to load markets');
    }
  };

  useEffect(() => {
    if (markets.length === 1) {
      const onlyMarket = markets[0].customerTypeId.toString();
      isLoadingMarketsRef.current = false;
      if (selectedMarket !== onlyMarket) {
        setSelectedMarket(onlyMarket);
        onFilterChange?.(selectedInstance, onlyMarket, searchTerm);
      } else {
        loadStores();
      }
    } 
    else {
      isLoadingMarketsRef.current = false;
      if (selectedInstance || selectedInstance === 'all') {
        loadStores();
      }
    }
  }, [markets]);

  const loadStores = async () => {
    setIsLoading(true);
    try {
      const filterOptions: MarketStoreFilterOptions = {
        companyId: selectedInstance || undefined,
        customerTypeId: selectedMarket || undefined,
        searchTerm: searchTerm || undefined,
        pageNumber: currentPage,
        pageSize: pageSize,
      };

      const response = await client.getMarketStores(filterOptions);

      if (response.success && response.data) {
        setStores(response.data.items || []);
        setTotalPages(response.data.totalPages || 0);
        setTotalItems(response.data.totalCount || 0);
      } else {
        setErrorMessage(response.message || t('loadErrorMessage'));
        setIsSubmittedSuccessfully(false);
        setShowSubmitStatusMessage(true);
      }
    } catch (error) {
      logError('Failed to load stores');
      setErrorMessage(t('loadErrorMessage'));
      setIsSubmittedSuccessfully(false);
      setShowSubmitStatusMessage(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchInputChange = (value: string) => {
    setSearchInput(value);
  };

  const handleSearchKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(0);
    onFilterChange?.(selectedInstance, selectedMarket, searchInput);
  };

  const handleInstanceChange = (event: SelectChangeEvent<string>) => {
    const newInstance = event.target.value;
    setSelectedInstance(newInstance);
    setSelectedMarket('');
    setCurrentPage(0);
    onFilterChange?.(newInstance, '');
  };

  const handleMarketChange = (event: SelectChangeEvent<string>) => {
    const newMarket = event.target.value;
    setSelectedMarket(newMarket);
    setCurrentPage(0);
    onFilterChange?.(selectedInstance, newMarket);
  };

  const handleClear = () => {
    setSearchInput('');
    setSearchTerm('');
    setSelectedInstance('all');
    setSelectedMarket('');
    setCurrentPage(0);
    onFilterChange?.('all', '', '');
  };

  const onClear = () => {
    handleClear();
  };

  const handleCloseMessage = () => {
    setShowSubmitStatusMessage(false);
  };

  const maxPages = () => {
    return Math.max(1, totalPages);
  };

  return (
    <>
      <Grid container
        spacing={5}>
        <Grid item
          lg={3}
          sm={6}
          xs={12}>
          <TextField
            label={t('search')}
            placeholder={t('searchPlaceholder')}
            value={searchInput}
            onChange={(event) => handleSearchInputChange(event.target.value)}
            onKeyPress={handleSearchKeyPress}
            fullWidth
          />
        </Grid>
        <Grid item
          lg={2}
          sm={3}
          xs={6}>
          <Button
            fullWidth
            onClick={handleClear}
            variant='secondary'
            size='lg'
          >
            {t('clear')}
          </Button>
        </Grid>
        <Grid item
          lg={2}
          sm={3}
          xs={6}>
          <Button
            fullWidth
            onClick={handleSearch}
            size='lg'
            startIcon={<SvgIcon><Search /></SvgIcon>}
          >
            {t('search')}
          </Button>
        </Grid>
        <Grid item
          lg={2.5}
          sm={6}
          xs={12}>
          <FormControl fullWidth>
            <InputLabel shrink>
              {t('filterByInstance')}
            </InputLabel>
            <Select
              value={instances.some(m => m.id?.toString() == selectedInstance) ? selectedInstance : ''}
              onChange={handleInstanceChange}
              label={t('filterByInstance')}
              displayEmpty
              notched
              renderValue={(value) => {
                if (value === '') {
                  return <span>{t('allInstances')}</span>;
                }

                return instances?.find(i => i.id.toString() === value)?.name || '';
              }}
            >
              <MenuItem value="all">
                {t('allInstances')}
              </MenuItem>
              {instances.map((instance) => (
                <MenuItem
                  key={instance.id}
                  value={instance.id.toString()}
                >
                  {instance.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item
          lg={2.5}
          sm={6}
          xs={12}>
          <FormControl fullWidth>
            <InputLabel shrink>
              {t('filterByMarket')}
            </InputLabel>
            <Select
              value={markets.some(m => m.customerTypeId?.toString() == selectedMarket) ? selectedMarket : ''}
              onChange={handleMarketChange}
              label={t('filterByMarket')}
              displayEmpty
              notched
              disabled={!selectedInstance}
            >
              <MenuItem value="">
                {t('allMarkets')}
              </MenuItem>
              {markets.map((market) => (
                <MenuItem
                  key={market.customerTypeId}
                  value={market.customerTypeId.toString()}
                >
                  {market.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item
          xs={12}>
          <Section>
            <StoresList
              list={stores}
              isLoading={isLoading}
              totalCount={totalItems}
              onClear={onClear}
              onCreateNew={onCreateNew}
            />
            {!isLoading && ( <Pagination
              page={currentPage}
              setPage={setCurrentPage}
              maxPages={maxPages()}
              breakpointForChangeDisplay={120000}
              useNoDataMessage={false}
            />
            )}
          </Section>
        </Grid>
      </Grid>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={showSubmitStatusMessage}
        onClose={handleCloseMessage}
        autoHideDuration={5000}
      >
        <Alert
          onClose={handleCloseMessage}
          severity={isSubmittedSuccessfully ? 'success' : 'error'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {isSubmittedSuccessfully ? (errorMessage || t('successMessage')) : errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default StoresTab;
